import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink } from 'react-router-dom';

import {
  Grid,
  Box,
  Typography,
  Button,
  Slider,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
} from '@material-ui/core';

import {
  MultiSelect,
  Select,
  OutlinedTextField,
  OutlinedTextArea,
  Editor
} from '../common/fields';
import FilePicker from '../common/filePicker';
import FilePickerMulti from '../common/filePickerMulti';
import Alert from '../common/alert';

import Markets from './Markets';
import ActionBar from './ActionBar';
import MainImage from './MainImage';
import MasterCountryTooltip from './MasterCountryTooltip';
import ImagePreview from './ImagePreview';

import { enqueueSnackbar } from '../snackbar/snackbarSlice';
import { selectGlobals } from '../app/appSlice';
import {
  selectMasterCountry,
  selectCurrentCountry,
  selectCountriesWithNames
} from '../countries/countriesSlice';
import {
  selectSaveStatus,
  clearSaveStatus,
  selectMaster,
  saveItemFamilyDraft,
  saveItemFamilyPublished,
  publishItemFamily,
  selectEqualStatus
} from './itemFamilySlice';

import { defaultStudioFolderPC24 } from '../../constants';
import BulletList from './BulletList';

const defaultStudioFolder = defaultStudioFolderPC24;

const ItemFamilyPagePC24 = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { itemOwnerId } = useParams();

  const { status, itemFamily, canEdit: userAllowedToEdit } = props;

  const { saveStatus, saveError } = useSelector(selectSaveStatus);
  const masterItemFamily = useSelector(selectMaster);
  const {
    usageGroups = [],
    equipments = [],
    suitableTerrains = [],
    features = [],
    seasons = [],
    brands = [],
    attributes = [],
    feeds = []
  } = useSelector(selectGlobals);
  const masterCountry = useSelector(selectMasterCountry);
  const currentCountry = useSelector(selectCurrentCountry);
  const allCountries = useSelector(selectCountriesWithNames);
  const equalStatus = useSelector(selectEqualStatus);

  const [ itemFamilyData, setItemFamilyData ] = useState(itemFamily || {});
  const [ hasUnsavedChanges, setHasUnsavedChanges ] = useState(false);
  const [ filteredEquipments, setFilteredEquipments ] = useState([]);

  const handleChange = (field, value) => {
    setItemFamilyData({
      ...itemFamilyData,
      [field]: value
    });
    setHasUnsavedChanges(true);
  }

  const handleFieldChange = event => {
    handleChange(event.target.name, event.target.value);
  }

  const saveDraft = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(saveItemFamilyDraft({itemOwnerId, itemFamilyId: itemFamily.code, itemFamilyData}));
    }
  };

  const saveDraftAndPublish = () => {
    // Save to server
    if(status === 'draft') {
      dispatch(publishItemFamily({itemOwnerId, itemFamilyId: itemFamily.code, itemFamilyData}));
    }
  }

  const savePublished = () => {
    // Save to server
    if(status === 'published') {
      dispatch(saveItemFamilyPublished({itemOwnerId, itemFamilyId: itemFamily.code, itemFamilyData}));
    }
  };

  const cancelChanges = () => {
    setItemFamilyData(itemFamily);
    setHasUnsavedChanges(false);
  };

  const renderTooltip = (content, type) => {
    if(!content || content.length === 0) {
      return null;
    }

    if(currentCountry === masterCountry) {
      return null;
    }

    return <MasterCountryTooltip content={content} type={type} masterCountry={masterCountry} />;
  }

  useEffect(() => {
    // Parent props change - basically draft vs publish change
    setItemFamilyData(itemFamily);
    setHasUnsavedChanges(false);
  }, [itemFamily]);

  useEffect(() => {
    if(typeof itemFamilyData.usageGroup === 'undefined') {
      return;
    }
    // Update equipments options if usage group changes
    const newEquipmentOptions = equipments.filter(equipment => {
      return equipment.usageGroups.some(val => itemFamilyData.usageGroup.includes(val));
    });
    setFilteredEquipments(newEquipmentOptions);

    // Also remove those selected equipments that do not belong to new usage groups
    const newEquipmentOptionIds = newEquipmentOptions.map(option => option.id);
    const newEquipmentSelection = itemFamilyData.equipment.filter(equipment => {
      return newEquipmentOptionIds.includes(equipment);
    });

    setItemFamilyData({
      ...itemFamilyData,
      equipment: newEquipmentSelection
    });

  }, [itemFamilyData.usageGroup]);

  useEffect(() => {
    // Show feedback on save status
    if(saveStatus === 'failed') {
      dispatch(enqueueSnackbar({
        message: t(`errors:${saveError.message}`),
        options: {
          variant: 'error'
        }
      }));
      dispatch(clearSaveStatus());
    } else if(saveStatus === 'succeeded') {
      dispatch(enqueueSnackbar({
        message: t('common:savedSuccessfully'),
        options: {
          variant: 'success'
        }
      }));
      dispatch(clearSaveStatus());
      setHasUnsavedChanges(false);
    }
  }, [saveStatus]);

  const eventProductCardPdfLink = itemFamilyData.pdfGenerationLinks?.find(link => link.id === 'event-product-card');
  const otherPdfLinks = itemFamilyData.pdfGenerationLinks?.filter(link => link.id !== 'event-product-card') || [];

  console.log('itemFamilyPage render', {eventProductCardPdfLink, fam: itemFamilyData.pdfGenerationLinks, status, hasUnsavedChanges});

  if(!itemFamily.code) {
    const message = status === 'published' ? 'notPublished' : 'notFound';
    return <Alert severity="info">{t(`itemFamily:${message}`)}</Alert>
  } else {

    return (
      <>
        <Grid container spacing={7}>
          <Grid item xs={12} md={8}>
            {status === 'draft' ? (equalStatus ? null : <Box mb={2}><Alert severity="info">{t('itemFamily:containsUnpunblishedChanges')}</Alert></Box>) : null}

            <Box mb={2}>
              <Typography variant="subtitle1">{t('itemFamily:itemFamily')}</Typography>
              <Typography variant="h4" component="h1">{itemFamilyData.name}</Typography>
            </Box>

            <Box mb={2}>
              <Typography variant="overline" display="block">{t('itemFamily:lastChanged')}</Typography>
              <Typography variant="caption">{itemFamilyData.updated}</Typography>
            </Box>

            <Box mb={4}>
              <Button variant="contained" color="primary" disableElevation component={RouterLink} to={`/pih/owner/${itemOwnerId}/items?itemFamilyCode=${encodeURIComponent(itemFamily.code)}`} target="_blank">{t('itemFamily:showItems')}</Button>
            </Box>

            <Box my={5}>
              <MultiSelect
                label={t('itemFamily:usageGroup')}
                name="usageGroup"
                value={itemFamilyData.usageGroup}
                options={usageGroups.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
              <Box my={3}>
                <MultiSelect
                  label={t('itemFamily:equipment')}
                  name="equipment"
                  value={itemFamilyData.equipment}
                  options={filteredEquipments.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('itemFamily:suitableTerrains')}
                  name="suitableTerrains"
                  value={itemFamilyData.suitableTerrains}
                  options={suitableTerrains.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
              <Box my={3}>
                <MultiSelect
                  label={t('itemFamily:features')}
                  name="features"
                  value={itemFamilyData.features}
                  options={features.map(item => ({label: item.name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

              <Box my={3}>
                <Select
                  label={t('itemFamily:season')}
                  name="season"
                  value={itemFamilyData.season}
                  options={seasons.map(item => ({label: item.name, value: item.code}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

              <Box my={3}>
                <Select
                  label={t('itemFamily:brand')}
                  name="brand"
                  value={itemFamilyData.brand}
                  options={brands.map(item => ({label: item.name, value: item.code}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>
            </Box>

            <Box my={5}>
              <Markets allCountries={allCountries} selectedCountries={itemFamilyData.markets} handleChange={newVal => handleChange('markets', newVal)} disabled={!userAllowedToEdit} />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:feeds')}</Typography></Box>
              <MultiSelect
                label={t('itemFamily:chooseFeeds')}
                name="feeds"
                value={itemFamilyData.feeds}
                options={feeds.map(item => ({label: item.name, value: item.id}))}
                onChange={handleFieldChange}
                disabled={!userAllowedToEdit}
              />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:content')}</Typography></Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:marketingName')}
                  name="marketingName"
                  value={itemFamilyData.marketingName}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingName)}
                  maxLength={50}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:marketingSlogan')}
                  name="marketingSlogan"
                  value={itemFamilyData.marketingSlogan}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingSlogan)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>

                <OutlinedTextField
                  label={t('itemFamily:marketingTextTitle')}
                  name="marketingTextTitle"
                  value={itemFamilyData.marketingTextTitle}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingTextTitle)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:vehicleTypeDescription')}
                  name="vehicleTypeDescription"
                  value={itemFamilyData.vehicleTypeDescription}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.vehicleTypeDescription)}
                  maxLength={120}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextArea
                  label={t('itemFamily:marketingTextShort')}
                  name="marketingTextShort"
                  value={itemFamilyData.marketingTextShort}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingTextShort)}
                  maxLength={250}
                />
              </Box>

              <Box my={3}>
                <Editor
                  label={t('itemFamily:marketingText')}
                  name="marketingText"
                  value={itemFamilyData.marketingText}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.marketingText, 'html')}
                  maxLength={500}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:metaTitle')}
                  name="metaTitle"
                  value={itemFamilyData.metaTitle}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.metaTitle)}
                  maxLength={60}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextArea
                  label={t('itemFamily:metaDescription')}
                  name="metaDescription"
                  value={itemFamilyData.metaDescription}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.metaDescription)}
                  maxLength={160}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:tagline')}
                  name="tagline"
                  value={itemFamilyData.tagline}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.tagline)}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextArea
                  label={t('item:loadCapacityComment')}
                  name="loadCapacityComment"
                  value={itemFamilyData.loadCapacityComment}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.loadCapacityComment)}
                  helperText={t('itemFamily:loadCapacityCommentHelp')}
                />
              </Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:application')}
                  name="application"
                  value={itemFamilyData.application}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.application)}
                />
              </Box>

              <Box my={3}>
                <Typography variant="h6" component="h3">{t('itemFamily:marketingBullets')}</Typography>
                <BulletList
                  tooltip={renderTooltip(masterItemFamily.marketingBullets, 'htmlList')}
                  data={itemFamilyData.marketingBullets || []}
                  userAllowedToEdit={userAllowedToEdit}
                  onEdit={(data) => handleChange('marketingBullets', data)}
                  addTitleText={t('itemFamily:addMarketingBullet')}
                  addButtonText={t('itemFamily:addMarketingBullet')}
                />
              </Box>



              <Box my={3}>
                <Typography variant="h6" component="h3">{t('itemFamily:backbone')}</Typography>
                <BulletList
                  tooltip={renderTooltip(masterItemFamily.backboneBullets, 'htmlList')}
                  data={itemFamilyData.backboneBullets}
                  userAllowedToEdit={userAllowedToEdit}
                  onEdit={(data) => handleChange('backboneBullets', data)}
                  addTitleText={t('itemFamily:addBackboneText')}
                  addButtonText={t('itemFamily:addBackboneText')}
                  maxLength={230}
                />
              </Box>

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:attributes')}</Typography></Box>

              <Box my={3}>
                <MultiSelect
                  label={t('itemFamily:chooseAttributes')}
                  name="attributes"
                  value={itemFamilyData.attributes}
                  options={attributes.attributes.map(item => ({label: item.global_name, value: item.id}))}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                />
              </Box>

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:images')}</Typography></Box>

              {['image25degreeFull', 'image25degreeHalf', 'imageFrontFull', 'imageFrontHalf', 'imageSideFull', 'imageCut3d'].map(field =>
                <FilePicker
                  key={field}
                  fieldName={t(`itemFamily:${field}`)}
                  fieldValue={itemFamilyData[field]}
                  setFieldValue={(file) => handleChange(field, file)}
                  disabled={!userAllowedToEdit}
                  rootFolderId={defaultStudioFolder}
                />
              )}

            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:moodImages')}</Typography></Box>

              <FilePickerMulti
                fieldName={t('itemFamily:moodImages')}
                fieldValue={itemFamilyData.moodImages}
                setFieldValue={(files) => handleChange('moodImages', files)}
                btnLabel={t('itemFamily:addImages')}
                disabled={!userAllowedToEdit}
                rootFolderId={defaultStudioFolder}
              />
            </Box>

            <Box my={5}>
              <Box mb={3}><Typography variant="h6" component="h2">{t('itemFamily:videos')}</Typography></Box>

              <Box my={3}>
                <OutlinedTextField
                  label={t('itemFamily:youtubeVideoUrl')}
                  name="youtubeVideoUrl"
                  value={itemFamilyData.youtubeVideoUrl}
                  onChange={handleFieldChange}
                  disabled={!userAllowedToEdit}
                  tooltip={renderTooltip(masterItemFamily.youtubeVideoUrl)}
                />
              </Box>

              <FilePicker
                fieldName={t('itemFamily:marketingVideo')}
                fieldValue={itemFamilyData.marketingVideo}
                setFieldValue={(file) => handleChange('marketingVideo', file)}
                disabled={!userAllowedToEdit}
              />

            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <MainImage src={itemFamilyData.imageMain?.medium} />

            <FilePicker
              fieldName={t('itemFamily:imageMain')}
              fieldValue={itemFamilyData.imageMain}
              setFieldValue={(file) => handleChange('imageMain', file)}
              disabled={!userAllowedToEdit}
              rootFolderId={defaultStudioFolder}
            />

            <Box mt={4}>
              <Box mb={2}><Typography variant="h6" component="h2">{status === 'draft' ? t('itemFamily:pdfSettings') : t('itemFamily:generatePdfs')}</Typography></Box>

              <Box border={1} borderColor="rgba(0, 0, 0, 0.1)" borderRadius={4} p={2}>
                <Box mb={2}><Typography variant="subtitle" component="h3">{t('itemFamily:eventProductCard')}</Typography></Box>
                <FilePicker
                  fieldName={t('itemFamily:backgroundImage')}
                  fieldValue={itemFamilyData.eventProductCardBackgroundImage}
                  setFieldValue={(file) => handleChange('eventProductCardBackgroundImage', file)}
                  disabled={!userAllowedToEdit}
                  rootFolderId={defaultStudioFolder}
                />
                <Typography id="discrete-slider" gutterBottom>{t('itemFamily:overlayOpacity')}</Typography>
                <Slider
                  min={0}
                  max={100}
                  value={typeof itemFamilyData.eventProductCardOverlayOpacity === 'number' ? itemFamilyData.eventProductCardOverlayOpacity : 80}
                  onChange={(e, value) => handleChange('eventProductCardOverlayOpacity', value)}
                  aria-labelledby="discrete-slider"
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => `${value}%`}
                  disabled={!userAllowedToEdit}
                />
                <ImagePreview src={itemFamilyData.eventProductCardBackgroundImage?.medium} opacity={itemFamilyData.eventProductCardOverlayOpacity} defaultOpacity={80} />
                {status === 'draft'
                  ? <Alert severity="info"><Typography variant="caption">{t('itemFamily:publishToGenerate')}</Typography></Alert>
                  : eventProductCardPdfLink
                    ? <form method="get" target="_blank" action={eventProductCardPdfLink.url}>
                      {eventProductCardPdfLink.url.split('?')[1]?.split('&').map(param => {
                        const [key, value] = param.split('=');
                        // we have separate radio controls for target.
                        if(key === 'target') return null;

                        let formattedValue = value;
                        if(key === 'familyCode') {
                          // Stage htmlLink encodes "IF-HAKKA TRUCK 844PLUS" as "IF-HAKKA+TRUCK+844PLUS"
                          // This passed back to pdf as queryparam double encodes it so it becomes "IF-HAKKA%2520TRUCK%2520844PLUS%2520" which won't find the family.
                          // So overriding with code here.
                          formattedValue = itemFamilyData.code;
                        }

                        return <input key={key} type="hidden" name={key} value={formattedValue} />
                      })}
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">{t('itemFamily:target')}</FormLabel>
                        <RadioGroup row aria-label="position" name="position" defaultValue="print">
                          <FormControlLabel value="end" control={<Radio color="primary" name="target" value="web" />} label={t('itemFamily:web')} />
                          <FormControlLabel value="end" control={<Radio color="primary" name="target" value="print" />} label={t('itemFamily:print')} />
                        </RadioGroup>
                      </FormControl>
                      <Box mt={2}><Button variant="contained" color="primary" disableElevation type="submit" disabled={hasUnsavedChanges}>{t('itemFamily:generate')}</Button></Box>
                    </form>
                    : null
                }
              </Box>
            </Box>

            {status === 'published' ? (
              <Box mt={4}>
                {otherPdfLinks?.map(link => <Box key={link.name} mb={2}><Button variant="contained" color="primary" disableElevation component={'a'} href={link.url} target="_blank">{link.name}</Button></Box>)}
              </Box>
            ) : null}
          </Grid>

        </Grid>
        {hasUnsavedChanges
          ?
          <ActionBar>
            <Button variant="outlined" color="primary" onClick={cancelChanges}>{t('common:cancelChanges')}</Button>
            {
              status === 'draft' ?
                <>
                  <Button variant="contained" color="primary" onClick={saveDraft} disabled={saveStatus === 'loading'}>{t('common:saveChanges')}</Button>
                  <Button variant="contained" color="primary" onClick={saveDraftAndPublish} disabled={saveStatus === 'loading'}>{t('common:saveAndPublishChanges')}</Button>
                </>
                :
                <Button variant="contained" color="primary" onClick={savePublished} disabled={saveStatus === 'loading'}>{t('common:publishChanges')}</Button>
            }

          </ActionBar>
          :
          null
        }
      </>
    );
  }
};

ItemFamilyPagePC24.propTypes = {
  status: PropTypes.oneOf(['draft', 'published']).isRequired,
  itemFamily: PropTypes.object
};

export default ItemFamilyPagePC24;
